import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
} from "@/components/ui/command";
import { useCommandMenu } from "./CommandMenuContext";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    ShoppingBag,
    Package,
    Users,
    Settings,
    Plus,
    FileText,
    Megaphone,
    Sun,
    Moon,
    Laptop,
    Clock,
    Loader2,
    TrendingUp,
    DollarSign,
    Mail,
    Phone
} from "lucide-react";
import { useTheme } from "next-themes";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { cn } from "@/lib/utils";

export const GlobalSearchDialog = () => {
    const { open, setOpen } = useCommandMenu();
    const navigate = useNavigate();
    const { setTheme } = useTheme();
    const [searchQuery, setSearchQuery] = useState("");

    // Use the global search hook
    const { data, isLoading, error } = useGlobalSearch(searchQuery, {
        type: 'all',
        limit: 5,
    });

    const runCommand = (command: () => void) => {
        setOpen(false);
        command();
    };

    const hasResults = data && data.results && data.results.total > 0;
    const hasProducts = data?.results?.products?.length > 0;
    const hasOrders = data?.results?.orders?.length > 0;
    const hasCustomers = data?.results?.customers?.length > 0;

    // Badge variant mapping
    const getBadgeVariant = (variant?: string) => {
        const mapping: Record<string, any> = {
            success: 'default',
            destructive: 'destructive',
            warning: 'secondary',
            default: 'outline',
            secondary: 'secondary',
        };
        return mapping[variant || 'default'] || 'outline';
    };

    return (
        <CommandDialog open={open} onOpenChange={setOpen}>
            <div className="flex items-center border-b px-3">
                <CommandInput
                    placeholder="ابحث عن طلبات، منتجات، عملاء، أو صفحات..."
                    className="text-right h-14 text-base border-0"
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                />
                {isLoading && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                )}
            </div>
            <CommandList className="text-right max-h-[500px]" dir="rtl">
                <CommandEmpty>
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                        <Package className="h-10 w-10 text-muted-foreground/50 mb-3" />
                        <p className="text-sm font-medium">
                            {error ? 'حدث خطأ في البحث' : 'لا توجد نتائج'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {error ? 'حاول مرة أخرى' : 'جرب البحث بكلمات مختلفة'}
                        </p>
                    </div>
                </CommandEmpty>

                {/* Recent Searches - shown when no query */}
                {!searchQuery && (
                    <CommandGroup heading="🕐 عمليات بحث سابقة">
                        <CommandItem onSelect={() => runCommand(() => navigate("/admin/products"))}>
                            <Clock className="ml-2 h-4 w-4 text-muted-foreground" />
                            <span>منتجات</span>
                        </CommandItem>
                        <CommandItem onSelect={() => runCommand(() => navigate("/admin/orders"))}>
                            <Clock className="ml-2 h-4 w-4 text-muted-foreground" />
                            <span>طلبات</span>
                        </CommandItem>
                    </CommandGroup>
                )}

                {/* Products Results */}
                {hasProducts && (
                    <>
                        <CommandGroup heading="📦 منتجات">
                            {data.results.products.map((product) => (
                                <CommandItem
                                    key={product.id}
                                    onSelect={() => runCommand(() => navigate(product.url))}
                                    className="py-3"
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        {product.image_url && (
                                            <img
                                                src={product.image_url}
                                                alt={product.name}
                                                className="h-10 w-10 rounded object-cover"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium truncate">{product.title}</span>
                                                {product.badge && (
                                                    <Badge variant={getBadgeVariant(product.badgeVariant)} className="text-xs">
                                                        {product.badge}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {product.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        <CommandSeparator />
                    </>
                )}

                {/* Orders Results */}
                {hasOrders && (
                    <>
                        <CommandGroup heading="🛒 طلبات">
                            {data.results.orders.map((order) => (
                                <CommandItem
                                    key={order.id}
                                    onSelect={() => runCommand(() => navigate(order.url))}
                                    className="py-3"
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <ShoppingBag className="h-5 w-5 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{order.title}</span>
                                                {order.badge && (
                                                    <Badge variant={getBadgeVariant(order.badgeVariant)} className="text-xs">
                                                        {order.badge}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {order.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        <CommandSeparator />
                    </>
                )}

                {/* Customers Results */}
                {hasCustomers && (
                    <>
                        <CommandGroup heading="👥 عملاء">
                            {data.results.customers.map((customer) => (
                                <CommandItem
                                    key={customer.id}
                                    onSelect={() => runCommand(() => navigate(customer.url))}
                                    className="py-3"
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                                            <Users className="h-5 w-5 text-accent-foreground" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium truncate">{customer.title}</span>
                                                {customer.badge && (
                                                    <Badge variant={getBadgeVariant(customer.badgeVariant)} className="text-xs">
                                                        {customer.badge}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {customer.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        <CommandSeparator />
                    </>
                )}

                {/* Navigation Links - always shown */}
                <CommandGroup heading="📄 روابط سريعة">
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin"))}>
                        <LayoutDashboard className="ml-2 h-4 w-4" />
                        <span>الرئيسية</span>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/orders"))}>
                        <ShoppingBag className="ml-2 h-4 w-4" />
                        <span>الطلبات</span>
                        <CommandShortcut>⌘O</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/products"))}>
                        <Package className="ml-2 h-4 w-4" />
                        <span>المنتجات</span>
                        <CommandShortcut>⌘P</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/customers"))}>
                        <Users className="ml-2 h-4 w-4" />
                        <span>العملاء</span>
                        <CommandShortcut>⌘C</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/analytics-unified"))}>
                        <FileText className="ml-2 h-4 w-4" />
                        <span>التقارير والتحليلات</span>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/settings"))}>
                        <Settings className="ml-2 h-4 w-4" />
                        <span>الإعدادات</span>
                        <CommandShortcut>⌘S</CommandShortcut>
                    </CommandItem>
                </CommandGroup>

                <CommandSeparator />

                <CommandGroup heading="⚡ إجراءات سريعة">
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/products/new"))}>
                        <Plus className="ml-2 h-4 w-4" />
                        <span>إضافة منتج جديد</span>
                        <CommandShortcut>⌘⇧P</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/orders/new"))}>
                        <Plus className="ml-2 h-4 w-4" />
                        <span>إنشاء طلب</span>
                        <CommandShortcut>⌘⇧O</CommandShortcut>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => navigate("/admin/marketing/campaigns/new"))}>
                        <Megaphone className="ml-2 h-4 w-4" />
                        <span>إنشاء حملة تسويقية</span>
                    </CommandItem>
                </CommandGroup>

                <CommandSeparator />

                <CommandGroup heading="🎨 المظهر">
                    <CommandItem onSelect={() => runCommand(() => setTheme("light"))}>
                        <Sun className="ml-2 h-4 w-4" />
                        <span>الوضع الفاتح</span>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => setTheme("dark"))}>
                        <Moon className="ml-2 h-4 w-4" />
                        <span>الوضع الداكن</span>
                    </CommandItem>
                    <CommandItem onSelect={() => runCommand(() => setTheme("system"))}>
                        <Laptop className="ml-2 h-4 w-4" />
                        <span>نظام الجهاز</span>
                    </CommandItem>
                </CommandGroup>
            </CommandList>
        </CommandDialog>
    );
};
